import { GetOrganizationDashboardQuery } from "@therapprove/common";
import {
    DashboardActionItem,
    DashboardMetrics,
    EMPTY_DASHBOARD_METRICS,
    SummaryMetricCard
} from "./types";

export const getDashboardMetrics = (
    data: GetOrganizationDashboardQuery | undefined
): DashboardMetrics => {
    const dashboard = data?.organizations?.organizationDashboard;
    if (!dashboard) return EMPTY_DASHBOARD_METRICS;

    return {
        referralPipelineCount: dashboard.referralPipelineCount ?? 0,
        pipelineReferralsWithoutAssignedProviderCount:
            dashboard.pipelineReferralsWithoutAssignedProviderCount ?? 0,
        inboundTriageCount: dashboard.inboundTriageCount ?? 0,
        staleReferralPipelineCount: dashboard.staleReferralPipelineCount ?? 0,
        globalReferralInvitePendingCount:
            dashboard.globalReferralInvitePendingCount ?? 0,
        directReferralCount: dashboard.directReferralCount ?? 0,
        directReferralsReceivedCount:
            dashboard.directReferralsReceivedCount ?? 0,
        websiteLeadCount: dashboard.websiteLeadCount ?? 0,
        websiteLeadsMovedToPipelineCount:
            dashboard.websiteLeadsMovedToPipelineCount ?? 0,
        pendingInviteCount: dashboard.pendingInviteCount ?? 0
    };
};

export const getDashboardActionItems = (
    metrics: DashboardMetrics
): DashboardActionItem[] => [
    {
        label: "Direct Referrals To Review",
        hint: "Direct referrals within the selected date range still need review. Open and accept or route to a provider.",
        count: metrics.directReferralCount,
        path: "/direct-referrals"
    },
    {
        label: "Website Leads To Review",
        hint: "Website inquiries within the selected date range are waiting. Review and invite the family.",
        count: metrics.websiteLeadCount,
        path: "/website-leads"
    },
    {
        label: "Referrals Needing Follow-up",
        hint: "Entries in the selected date range with no updates in 7 days. Check status and follow up.",
        count: metrics.staleReferralPipelineCount,
        path: "/referral-pipeline?filter=needs-follow-up"
    },
    {
        label: "Referrals Without Assigned Provider",
        hint: "Entries in the selected date range still waiting for assignment. Assign a provider now.",
        count: metrics.pipelineReferralsWithoutAssignedProviderCount,
        path: "/referral-pipeline?filter=without-assigned-provider"
    },
    {
        label: "Pending Invite Follow-ups",
        hint: "Invites updated in the selected date range and unanswered after 3 days. Resend the invite.",
        count: metrics.pendingInviteCount,
        path: "/invites#clinicinvites"
    },
    {
        label: "Global Invites Awaiting Response",
        hint: "Global invites updated in the selected date range and unanswered after 3 days. Resend the invite.",
        count: metrics.globalReferralInvitePendingCount,
        path: "/invites#globalinvites"
    }
];

export const getDashboardSummaryCards = (
    metrics: DashboardMetrics
): SummaryMetricCard[] => [
    {
        label: "Referral Pipeline",
        value: metrics.referralPipelineCount,
        hint: "Entries added to your pipeline within the selected date range",
        accent: "primary"
    },
    {
        label: "Direct Referrals Received",
        value: metrics.directReferralsReceivedCount,
        hint: "Direct referrals your organization received within the selected date range",
        accent: "secondary"
    },
    {
        label: "Website Leads",
        value: metrics.websiteLeadsMovedToPipelineCount,
        hint: "Website leads within the selected date range that have been moved into your pipeline",
        accent: "violet"
    }
];
